/*
  Earnest : plan de protection de la fiche produit (bloc eh_protection, snippets/eh-product-block.liquid).
  docs/theme-2026-10-29.md, 4.1.
  - « 1 year · Included » est le seul choix coché, au chargement comme au retour sur la page (pageshow) :
    une option payante n'est jamais cochée d'avance, ni par le code, ni par une URL, ni par un souvenir de visite.
  - À l'ajout au panier : si un plan payant est choisi, on ajoute d'abord le plan (/cart/add.js, même quantité,
    propriété visible « For: JH-D26AT hearing aids »), puis on laisse le formulaire de Dawn ajouter l'appareil et
    ouvrir le tiroir. Si le plan n'entre pas, l'appareil n'est pas ajouté et un message clair s'affiche.
  - Le prix du bouton (« Add to cart · $348 ») suit le choix ; l'événement « eh:plan-change » prévient la barre
    collante (assets/eh-sticky-atc.js).
*/
(() => {
  if (!window.customElements || customElements.get('eh-protection')) return;

  // Montant en cents → texte selon le format de la boutique, sans « .00 » (comme money_without_trailing_zeros).
  const formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const value = Number(cents || 0) / 100;
    const whole = Number.isInteger(value);
    const group = (n, sep) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const build = (thousands, decimal, decimals) => {
      const fixed = value.toFixed(decimals);
      const [int, dec] = fixed.split('.');
      return group(int, thousands) + (dec && !whole ? decimal + dec : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return build(',', '.', 0);
        case 'amount_with_comma_separator':
          return build('.', ',', 2);
        case 'amount_no_decimals_with_comma_separator':
          return build('.', ',', 0);
        case 'amount_with_apostrophe_separator':
          return build("'", '.', 2);
        default:
          return build(',', '.', 2);
      }
    });
  };
  window.ehFormatMoney = formatMoney;

  class EhProtection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.bypass = false;

      this.onChange = () => this.update();
      this.radios.forEach((r) => r.addEventListener('change', this.onChange));

      // Retour sur la page (cache du navigateur) : on remet « Included ».
      this.onPageShow = () => this.reset();
      window.addEventListener('pageshow', this.onPageShow);

      // Changement de variante (Dawn remplace le prix et le texte du bouton) : on recalcule.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => window.setTimeout(() => this.update(), 50));
      }

      this.bindForm();
      this.reset();
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.onPageShow);
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      if (this.productForm && this.onSubmit) this.productForm.removeEventListener('submit', this.onSubmit, true);
    }

    reset() {
      this.radios.forEach((r) => {
        r.checked = r.value === '';
      });
      this.update();
    }

    selected() {
      return this.radios.find((r) => r.checked) || null;
    }

    devicePriceCents() {
      const el = document.querySelector(`#price-${this.sectionId} [data-eh-price-cents]`);
      return el ? Number(el.dataset.ehPriceCents) : 0;
    }

    mainButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    // Le texte du bouton : le gabarit vient du réglage « button_label » (data-eh-add-template), {price} = total.
    update() {
      const choice = this.selected();
      const planCents = choice && choice.value ? Number(choice.dataset.price || 0) : 0;
      const deviceCents = this.devicePriceCents();
      const total = deviceCents + planCents;
      const button = this.mainButton();
      const label = button && button.querySelector('[data-eh-add-label]');
      if (label && label.dataset.ehTemplate) {
        label.textContent = label.dataset.ehTemplate.split('{price}').join(formatMoney(total, this.moneyFormat));
      }
      document.dispatchEvent(
        new CustomEvent('eh:plan-change', {
          detail: {
            sectionId: this.sectionId,
            planVariantId: choice && choice.value ? Number(choice.value) : null,
            planCents,
            deviceCents,
            totalCents: total,
            format: this.moneyFormat,
          },
        })
      );
    }

    bindForm() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      this.productForm = form ? form.closest('product-form') : null;
      if (!form || !this.productForm) return;
      this.form = form;
      // Phase de capture sur product-form : notre écouteur passe avant celui de Dawn (posé sur le formulaire).
      this.onSubmit = (event) => this.handleSubmit(event);
      this.productForm.addEventListener('submit', this.onSubmit, true);
    }

    handleSubmit(event) {
      if (this.bypass) return;
      const choice = this.selected();
      if (!choice || !choice.value) return;
      const button = this.mainButton();
      if (button && (button.getAttribute('aria-disabled') === 'true' || button.disabled)) return;

      event.preventDefault();
      event.stopPropagation();

      const quantityInput = this.form.querySelector('[name="quantity"]') || document.querySelector(`#Quantity-${this.sectionId}`);
      const quantity = Math.max(1, parseInt(quantityInput ? quantityInput.value : '1', 10) || 1);
      const properties = {};
      if (this.dataset.forName && this.dataset.forValue) properties[this.dataset.forName] = this.dataset.forValue;

      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }
      this.showError('');

      const root = (window.routes && window.routes.cart_add_url) || '/cart/add';
      fetch(`${root}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(choice.value), quantity, properties }] }),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok && !data.status, data })))
        .then(({ ok }) => {
          this.release(button);
          if (!ok) {
            this.showError(this.dataset.error || 'We could not add the protection plan.');
            return;
          }
          // Le plan est dans le panier : Dawn ajoute maintenant l'appareil et ouvre le tiroir (qui montre les deux).
          this.bypass = true;
          try {
            if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
            else this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          } finally {
            this.bypass = false;
          }
        })
        .catch(() => {
          this.release(button);
          this.showError(this.dataset.error || 'We could not add the protection plan.');
        });
    }

    release(button) {
      if (!button) return;
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }

    // Même emplacement que les erreurs de Dawn (au-dessus du bouton, role="alert").
    showError(message) {
      const wrapper = this.productForm && this.productForm.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) return;
      const text = wrapper.querySelector('.product-form__error-message');
      if (!message) {
        wrapper.toggleAttribute('hidden', true);
        return;
      }
      if (text) text.textContent = message;
      wrapper.toggleAttribute('hidden', false);
    }
  }

  customElements.define('eh-protection', EhProtection);
})();
